import { $http } from '@/common/api/http.service';
import { $httpFhirApi } from '@/common/api/httpFhir.service';
import { $log } from '@/common/api/log.service';
import { useAuthStore } from '@/stores/auth';
import { Patient } from '@/fhirworks';

import { usePatientAccess } from '@/composables/usePatientAccess';
import { extractAidboxQueryData } from '@/composables/useAidboxQuery';

// "First L" for a portal email greeting. A one-word name stays whole instead of throwing.
function shortName(name) {
    if (!name) return 'User';
    const parts = name.trim().split(/\s+/);
    if (parts.length < 2) return parts[0] || 'User';
    return `${parts[0]} ${parts[1][0]}`;
}

function getTransactionQuery(queryString) {
    queryString = encodeURI(queryString.split(' ').filter(Boolean).join('+'));

    return 'Patient/$lookup?by=name.family,name.given,birthDate,identifier.value;telecom.value&inquiryStatus:not=draft,pending,rejected,merged&sort=name.given,name.family&count=50&q=' + queryString;
}

async function processPatientResponse(returnedPatients, enforceAccess = true) {
    if (!returnedPatients) return [];

    // TODO: Change when AidBox LookUp function is updated to allow filtering
    // Filter out patients with inquiryStatus of 'draft', 'pending' or 'rejected' (patients without inquiryStatus are included)
    returnedPatients = returnedPatients.filter(
        (patient) => patient.resourceType !== 'Patient' || !patient.inquiryStatus || !['draft', 'pending', 'rejected', 'merged'].includes(patient.inquiryStatus),
    );

    // get list of id's
    let idList = returnedPatients
        .filter((el) => el.resourceType === 'Patient')
        .map((el) => el.id)
        .join(',');

    if (enforceAccess && idList) {
        const authStore = useAuthStore();
        const roles = authStore.practitionerRoles;

        let includeStatement = '';
        if (roles?.some((role) => role.onlyCareTeam)) {
            includeStatement = '&_revinclude=CareTeam:encounter';
        }

        // Enrichment, so it never takes the search down with it. This read failing used to reject
        // the whole promise, which left every caller with no results and a spinner that never
        // stopped - a search that answers nothing is worse than one that answers unannotated.
        try {
            let ret = await $httpFhirApi.get(
                'Encounter?_elements=status,healthcareService,subject,location' +
                    '&part-of:missing=true' +
                    '&episode-of-care:missing=false' +
                    '&status=planned,arrived,triaged,in-progress,onleave' +
                    '&subject=' +
                    idList +
                    includeStatement,
            );

            const { addProgramsToContacts } = usePatientAccess();
            addProgramsToContacts(returnedPatients, ret.data);
        } catch (err) {
            // Marked unknown, never left unenrolled. hasAccessToContact GRANTS a patient carrying
            // no programs, so a failed read that simply returned the results unannotated would
            // turn every access-gated search in the app into an open one. Degrading gracefully
            // and degrading permissively are not the same thing.
            returnedPatients.forEach((patient) => {
                if (patient.resourceType === 'Patient') patient.temp.programsUnknown = true;
            });
            $log.error('patient.api: could not read where these patients are enrolled', err?.message, err?.stack);
        }
    }

    return returnedPatients;
}

export default {
    getTransactionQuery: getTransactionQuery,
    processPatientResponse: processPatientResponse,
    inviteToPortal: (subject, config) => {
        const authStore = useAuthStore();
        if (!['Patient'].includes(subject.resourceType)) {
            throw new Error("Only Patient's and Person's can be portal users");
        }

        return $http.post(
            '/api/account/' + authStore.account.id + '/invite-portal-user',
            {
                subjectType: subject.resourceType,
                subjectId: subject.id,
                email: subject.primaryEmailValue,
            },
            config,
        );
    },
    getPortalStatus: async (subject, config) => {
        const authStore = useAuthStore();
        const response = await $http.get('/api/account/' + authStore.account.id + '/portal-status', {
            ...config,
            params: {
                subjectType: subject.resourceType,
                subjectId: subject.id,
            },
        });

        return response.data;
    },
    /**
     * Email a packet to the person completing it. With `resend`, the server also cancels their
     * earlier unused invitation links once this email is sent (#6434).
     */
    sendPatientPacketEmail: (subject, regarding, config, { resend = false } = {}) => {
        const authStore = useAuthStore();
        if (!['Patient'].includes(subject.resourceType)) {
            throw new Error('Not a patient');
        }

        return $http.post(
            '/api/account/' + authStore.account.id + '/send-portal-packet-email',
            {
                subjectType: subject.resourceType,
                subjectId: subject.id,
                subjectName: shortName(subject.name),
                email: subject.email,
                regardingName: shortName(regarding.name),
                portalUpdate: subject.portalUpdate,
                resend,
            },
            config,
        );
    },
    /**
     * Send portalized document email to a related contact
     * @param {Object} contact - The contact to send the email to (Patient or Person)
     * @param {Object} patient - The patient whose document is being shared
     * @param {Object} config - Optional axios config
     */
    sendPortalizedDocumentEmail: (contact, patient, config) => {
        const authStore = useAuthStore();

        const contactName = shortName(contact.name);
        const patientName = shortName(patient.name);

        if (!['Patient', 'Person'].includes(contact.resourceType)) {
            throw new Error('Contact must be a Patient or Person');
        }

        return $http.post(
            '/api/account/' + authStore.account.id + '/send-portal-packet-email',
            {
                subjectType: contact.resourceType,
                subjectId: contact.id,
                subjectName: contactName,
                email: contact.email,
                regardingName: patientName,
                portalUpdate: false,
                isPortalized: true,
            },
            config,
        );
    },
    /**
     * Find potential duplicate patients using fuzzy/trigram matching
     * Handles misspellings and name variations
     * @param {Object} patient - Patient data to check for duplicates
     * @param {Object} options - Optional settings { threshold: 0.3 }
     * @returns {Promise<Array>} - Array of potential matches with confidence scores
     */
    findDuplicates: async (patient, options = {}) => {
        const { threshold = 0.3 } = options;

        // Extract search parameters from patient data
        const family = patient.name?.[0]?.family || null;
        const given = patient.name?.[0]?.given?.[0] || null;
        const birthDate = patient.birthDate || null;

        // Build query params
        const params = new URLSearchParams();
        if (family) params.append('family', family);
        if (given) params.append('given', given);
        if (birthDate) params.append('birthDate', birthDate);
        params.append('threshold', threshold.toString());
        params.append('_count', '10');

        const response = await $httpFhirApi.get(`/$query/fuzzyPatientSearch?${params.toString()}`);

        const data = extractAidboxQueryData(response);
        return (
            data.map((row) => {
                const result = row.result || row;
                const resource = result.resource || result;
                return {
                    patient: new Patient({ ...resource, id: result.id }),
                    similarityScore: result.similarity_score || 0,
                };
            }) || []
        );
    },
};
